import React from "react";
import { Button, Card, Collapse } from "react-bootstrap";
import { FiChevronDown } from "react-icons/fi";
import { Task } from "./ProjectConfigurationTypes.ts";
import SubtaskComponent from "./SubtaskComponent.tsx";

interface TaskComponentProps {
  task: Task;
  phaseIndex: number;
  partIndex: number;
  taskIndex: number;
  taskId: string;
  isExpanded: boolean;
  projectId: string;
  subjects: any[];
  topicsCache: Record<string, any[]>;
  contentStatus?: {
    video?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    notes?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    mcq?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    project_coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  } | null;
  dragHandleProps: any;
  onToggleExpand: () => void;
  onPreviewContent: (subtaskIndex: number, type: "video" | "notes", content: any) => void;
  onFetchTopics: (subjectId: string) => Promise<void>;
  isSubtaskExpanded: (subtaskId: string) => boolean;
  toggleSubtaskExpand: (subtaskId: string) => void;
}

/**
 * TaskComponent: Renders a task card with drag-and-drop support for subtasks
 * This component displays a single task within a part and manages its subtasks.
 * It provides functionality for:
 * - Expanding/collapsing the task
 * - Editing the task name
 * - Adding new subtasks
 * - Deleting the task
 * - Drag-and-drop reordering of subtasks
 * - Rendering SubtaskComponent children for each subtask
 * - Managing available topics and subtopics based on selected subject
 */
const TaskComponent: React.FC<TaskComponentProps> = ({
  task,
  phaseIndex,
  partIndex,
  taskIndex,
  taskId,
  isExpanded,
  projectId,
  subjects,
  topicsCache,
  contentStatus,
  dragHandleProps,
  onToggleExpand,
  onPreviewContent,
  onFetchTopics,
  isSubtaskExpanded,
  toggleSubtaskExpand
}) => {
  return (
    <Card className="mb-2">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          
          <Button
            variant="light"
            size="sm"
            className="p-1"
            onClick={onToggleExpand}
          >
            <FiChevronDown
              size={18}
              style={{
                transition: "transform 0.2s",
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)"
              }}
            />
          </Button>
          <span className="fw-bold me-2">{task.id}</span>
          <span>{task.name || "—"}</span>
        </div>
        
      </Card.Header>
      <Collapse in={isExpanded}>
        <Card.Body>
          {task.subtasks.map((subtask, subtaskIndex) => {
            const subtaskId = `${taskId}-${subtask.id}`;
            const topicsFromCache = subtask.subjectId ? topicsCache[subtask.subjectId] : null;
            const availableTopics = Array.isArray(topicsFromCache) ? topicsFromCache : [];
            const selectedTopic = availableTopics.find(t => t.topic_id === subtask.topicId);
            const availableSubtopics = Array.isArray(selectedTopic?.sub_topics) ? selectedTopic.sub_topics : [];
            
            return (
              <div key={subtask.id}>
                <SubtaskComponent
                  subtask={subtask}
                  phaseIndex={phaseIndex}
                  partIndex={partIndex}
                  taskIndex={taskIndex}
                  subtaskIndex={subtaskIndex}
                  subtaskId={subtaskId}
                  taskId={taskId}
                  isExpanded={isSubtaskExpanded(subtaskId)}
                  projectId={projectId}
                  subjects={subjects}
                  availableTopics={availableTopics}
                  availableSubtopics={availableSubtopics}
                  contentStatus={contentStatus}
                  dragHandleProps={undefined}
                  onToggleExpand={() => toggleSubtaskExpand(subtaskId)}
                  onPreviewContent={(type, content) => onPreviewContent(subtaskIndex, type, content)}
                  onFetchTopics={onFetchTopics}
                />
              </div>
            );
          })}
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default TaskComponent;

